import {
  CreateCustomerPlanDiscountFactory,
  CustomerDiscountFactory,
  CustomerSubscriptionPlanFactory,
  CustomerUserSettingsFactory,
  InvoiceSummaryFactory,
  invalidateCardGroup,
  invalidateCustomerDiscounts,
  invalidateCustomerInvoice,
  invalidateCustomerPlanDiscount,
  invalidateUserSettings,
  SelectCardGroupFactory,
} from '@domains/billing-settings'
import {
  invalidateCustomerTemplate,
  invalidateHasTemplateAnchoredToSignupPrice,
  useCustomerTemplate,
  useHasTemplateAnchoredToSignupPrice,
} from '@domains/subscription'
import { createCustomTheme } from '@domains/subscription/theme'
import { Alert, Stack, ThemeProvider, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

interface BillingSettingsV3Props {
  customerId: string
  currency: string
}

export const BillingSettingsV3 = ({ customerId, currency }: BillingSettingsV3Props) => {
  const queryClient = useQueryClient()
  const { data: customerTemplate } = useCustomerTemplate(customerId)
  const { data: hasAnchoredToSignupPrice } = useHasTemplateAnchoredToSignupPrice(customerTemplate?.id ?? '')

  const handleTemplateChange = () => {
    invalidateCustomerDiscounts({ queryClient, customerId })
    invalidateCardGroup({ queryClient, customerId })
    invalidateCustomerPlanDiscount({ queryClient, customerId })
    invalidateUserSettings({ queryClient, customerId })
    invalidateCustomerInvoice({ queryClient, customerId })
    invalidateHasTemplateAnchoredToSignupPrice({ queryClient, templateId: customerTemplate?.id })
    invalidateCustomerTemplate({ queryClient, customerId })
  }

  const onPlanDiscountChange = () => {
    invalidateCustomerInvoice({ queryClient, customerId })
  }

  const onUserSettingsChange = () => {
    invalidateCustomerInvoice({ queryClient, customerId })
  }

  const onCustomerDiscountChange = () => {
    invalidateCustomerInvoice({ queryClient, customerId })
  }

  return (
    <ThemeProvider theme={createCustomTheme()}>
      <Stack
        sx={{
          paddingBottom: 2,
          gap: 2,
        }}
      >
        {hasAnchoredToSignupPrice && (
          <Alert severity="warning">
            This customer has a price that is anchored to their signup date. Due to a limitation in the billing system,{' '}
            <b>
              <u>
                any prices changed on this page will automatically bill the customer monthly instead of being aligned with their signup date
              </u>
            </b>
            .
          </Alert>
        )}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h2">Billing settings</Typography>
          <InvoiceSummaryFactory customerId={customerId} />
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            flex: 1,
          }}
        >
          <CustomerSubscriptionPlanFactory customerId={customerId} onChange={handleTemplateChange} />
          <CreateCustomerPlanDiscountFactory customerId={customerId} currency={currency} onChange={onPlanDiscountChange} />
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: 2,
            flex: 1,
          }}
        >
          <SelectCardGroupFactory customerId={customerId} />
          <CustomerUserSettingsFactory customerId={customerId} onChange={onUserSettingsChange} />
        </Stack>
        <CustomerDiscountFactory customerId={customerId} currency={currency} onChange={onCustomerDiscountChange} />
      </Stack>
    </ThemeProvider>
  )
}
